import { ReactElement } from 'react';
import styled from 'styled-components';

import { createTranslatedText, TranslatableText, useTranslate } from '@dreryk/react-common';
import {
   appendTestableComponentAttributes,
   basicCaptionTextStyle,
   ContentNodeRenderer,
   fonts,
   TestableComponentProps,
   Tooltip,
   TooltipTrigger,
} from '@dreryk/react-components';

const StyledPrescriptionTextValue = styled.div`
   display: flex;
   gap: 4px;
   justify-content: space-between;
   width: 192px;
`;

StyledPrescriptionTextValue.displayName = 'StyledPrescriptionTextValue';

const StyledPrescriptionTextLabel = styled.span`
   ${basicCaptionTextStyle}
   color: ${(props) => props.theme.borderDark};
   white-space: nowrap;
`;

StyledPrescriptionTextLabel.displayName = 'StyledPrescriptionTextLabel';

const StyledPrescriptionTextTooltip = styled(Tooltip)`
   ${basicCaptionTextStyle}
   white-space: nowrap;
   overflow: hidden;
   text-overflow: ellipsis;
`;

StyledPrescriptionTextTooltip.displayName = 'StyledPrescriptionTextTooltip';

const StyledPrescriptionTooltipElement = styled.span`
   ${basicCaptionTextStyle}
   color: ${(props) => props.theme.textBody};
   font-weight: ${fonts.fontWeightBold};
`;

StyledPrescriptionTooltipElement.displayName = 'StyledPrescriptionTooltipElement';

type PrescriptionTextValueProps = {
   readonly label: TranslatableText;
   readonly value: TranslatableText | ReactElement | undefined;
   readonly tooltipContent?: ReactElement;
} & TestableComponentProps;

export const PrescriptionTextValue = (props: PrescriptionTextValueProps): JSX.Element => {
   const { label, tooltipContent, testId, value = createTranslatedText('-') } = props;
   const translate = useTranslate();

   return (
      <StyledPrescriptionTextValue>
         <StyledPrescriptionTextLabel>
            {translate(label)}
            {':'}
         </StyledPrescriptionTextLabel>
         <StyledPrescriptionTextTooltip
            trigger={TooltipTrigger.HOVER}
            testId={testId}
            maxWidth={'400px'}
            content={tooltipContent}
            disabled={!tooltipContent}
         >
            <StyledPrescriptionTooltipElement {...appendTestableComponentAttributes('PrescriptionTextValue', testId)}>
               <ContentNodeRenderer node={value} />
            </StyledPrescriptionTooltipElement>
         </StyledPrescriptionTextTooltip>
      </StyledPrescriptionTextValue>
   );
};
